import { Pipe, PipeTransform, Injectable, OnDestroy } from '@angular/core';
import { RoundingPolicy, RoundingSettings } from 'src/app/core/interfaces/rounding-settings.interface';
import { RoundingSettingsService } from 'src/app/modules/settings/services/rounding-settings.service';
import { Subject, takeUntil } from 'rxjs';

/**
 * RoundingPipe - Rounds numbers based on the current rounding settings
 * 
 * Usage:
 * - {{ value | rounding }} - Uses current settings from service cache
 * - {{ value | rounding:precision }} - Uses custom precision with current rounding policy
 * - {{ value | rounding:precision:policy }} - Uses custom precision and rounding policy
 * - {{ value | rounding:precision:policy:tolerance }} - Uses custom precision, policy, and tolerance
 * 
 * Examples:
 * - {{ 123.456789 | rounding }} → 123.46 (using current settings)
 * - {{ 123.456789 | rounding:3 }} → 123.457 (3 decimal places)
 * - {{ 123.456789 | rounding:2:'Up' }} → 123.46 (2 decimal places, round up)
 * - {{ 123.456789 | rounding:2:'Up':0.01 }} → 123.46 (with tolerance)
 */
@Pipe({
  name: 'rounding',
  pure: false // Make it impure to react to service changes
})
@Injectable()
export class RoundingPipe implements PipeTransform, OnDestroy {
  private destroy$ = new Subject<void>();
  private currentSettings: RoundingSettings | null = null;

  constructor(private roundingSettingsService: RoundingSettingsService) {
    // Subscribe to settings changes for real-time updates
    this.roundingSettingsService.getCurrentRoundingSettings$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(settings => {
        this.currentSettings = settings;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  transform(
    value: number | string | undefined, 
    displayPrecision?: number, 
    roundingPolicy?: RoundingPolicy,
    roundingTolerance?: number
  ): any {
    const rounded = this.roundingSettingsService.round(value, {
      displayPrecision,
      roundingPolicy,
      roundingTolerance
    });

    const precision = displayPrecision ?? this.currentSettings?.displayPrecision ?? 2;
    
    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
  }
}
